import { GoalPeriod } from '@akin-home/amplify';
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  isSameDay,
  isSameMonth,
  startOfWeek,
} from 'date-fns';
import { Goal } from '../types';

interface Params {
  toDate: Date;

  user_id: string;

  sample_count: number;
}

// Gets a sample of the completion rate for a goal over time
export function getGoalCompletionRateSample(
  this: Goal,
  { toDate, user_id, sample_count }: Params
): number[] {
  switch (this.period) {
    case GoalPeriod.day:
      return getDailyCompletionRateSample(this, {
        toDate,
        user_id,
        sample_count,
      });
    case GoalPeriod.week:
      return getWeeklyCompletionRateSample(this, {
        toDate,
        user_id,
        sample_count,
      });
    case GoalPeriod.month:
      return getMonthlyCompletionRateSample(this, {
        toDate,
        user_id,
        sample_count,
      });
    default:
      return [];
  }
}

const getDailyCompletionRateSample = (
  goal: Goal,
  { toDate, user_id, sample_count }: Params
): number[] => {
  const start = goal.start || new Date();

  if (isSameDay(start, toDate))
    return [goal.getCompletionRate(toDate, user_id)];

  const days = eachDayOfInterval({
    start,
    end: toDate,
  });

  return getSamples(goal, days, sample_count, user_id);
};

const getWeeklyCompletionRateSample = (
  goal: Goal,
  { toDate, user_id, sample_count }: Params
): number[] => {
  const start = startOfWeek(goal.start || new Date());
  const end = startOfWeek(toDate);

  if (end < start) return [goal.getCompletionRate(end, user_id)];

  const weeks = eachWeekOfInterval({
    start,
    end,
  });

  return getSamples(goal, weeks, sample_count, user_id);
};

const getMonthlyCompletionRateSample = (
  goal: Goal,
  { toDate, user_id, sample_count }: Params
): number[] => {
  const start = goal.start || new Date();

  if (isSameMonth(start, toDate))
    return [goal.getCompletionRate(toDate, user_id)];

  const months = eachMonthOfInterval({
    start,
    end: toDate,
  }).filter((month) => month >= start);

  return getSamples(goal, months, sample_count, user_id);
};

const getSamples = (
  goal: Goal,
  dates: Date[],
  sample_count: number,
  user_id: string
) => {
  const steps = Math.floor(dates.length / sample_count);

  return [...new Array(sample_count)]
    .map((_, idx) => {
      const samplePoint = (idx + 1) * steps;

      return dates[samplePoint]
        ? goal.getCompletionRate(dates[samplePoint], user_id)
        : undefined;
    })
    .filter(Boolean) as number[];
};
